import React, { useState } from 'react';
import { Table, Tag, Input, DatePicker, Space, Card, Statistic, Row, Col } from 'antd';
import { DollarOutlined, CalendarOutlined } from '@ant-design/icons';
import { formatCurrency, formatDate } from '../../../../shared/utils/formatters';
import { useDebounce } from '../../../../shared/hooks/useDebounce';
import dayjs from 'dayjs';
import styles from './PaymentHistory.module.css';

const { Search } = Input;
const { RangePicker } = DatePicker;

export default function PaymentHistory({ payments, debts, loading }) {
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState([]);
    const debouncedSearchText = useDebounce(searchText, 300);

    // Lọc payments
    const filteredPayments = payments.filter(payment => {
        // Lọc theo text
        if (debouncedSearchText) {
            const searchLower = debouncedSearchText.toLowerCase();
            const matchText =
                payment.nguoiVay.toLowerCase().includes(searchLower) ||
                payment.soTienTra.toString().includes(searchLower) ||
                (payment.ghiChu && payment.ghiChu.toLowerCase().includes(searchLower));

            if (!matchText) return false;
        }

        // Lọc theo ngày
        if (dateRange.length === 2) {
            const paymentDate = dayjs(payment.ngayTra);
            const [startDate, endDate] = dateRange;
            if (!paymentDate.isBetween(startDate, endDate, 'day', '[]')) {
                return false;
            }
        }

        return true;
    });

    // Tính thống kê
    const calculateStats = () => {
        const totalPayments = filteredPayments.reduce((sum, payment) => sum + payment.soTienTra, 0);
        const thisMonthPayments = payments.filter(payment =>
            dayjs(payment.ngayTra).format('YYYY-MM') === dayjs().format('YYYY-MM')
        ).reduce((sum, payment) => sum + payment.soTienTra, 0);

        return {
            totalPayments,
            thisMonthPayments,
            paymentCount: filteredPayments.length,
            thisMonthCount: payments.filter(payment =>
                dayjs(payment.ngayTra).format('YYYY-MM') === dayjs().format('YYYY-MM')
            ).length
        };
    };

    const stats = calculateStats();

    const columns = [
        {
            title: 'Ngày trả',
            dataIndex: 'ngayTra',
            key: 'ngayTra',
            render: (date) => (
                <div className={styles.dateColumn}>
                    <div className={styles.date}>{formatDate(date)}</div>
                    <div className={styles.time}>{dayjs(date).format('HH:mm')}</div>
                </div>
            ),
            sorter: (a, b) => new Date(a.ngayTra) - new Date(b.ngayTra),
            width: 120,
        },
        {
            title: 'Người cho vay',
            dataIndex: 'nguoiVay',
            key: 'nguoiVay',
            width: 150,
            ellipsis: true,
        },
        {
            title: 'Số tiền trả',
            dataIndex: 'soTienTra',
            key: 'soTienTra',
            render: (amount) => (
                <div className={styles.paymentAmount}>
                    <Tag color="success" icon={<DollarOutlined />}>
                        +{formatCurrency(amount)}
                    </Tag>
                </div>
            ),
            sorter: (a, b) => a.soTienTra - b.soTienTra,
            width: 150,
        },
        {
            title: 'Ghi chú',
            dataIndex: 'ghiChu',
            key: 'ghiChu',
            ellipsis: true,
            render: (text) => text || <span className={styles.noNote}>Không có ghi chú</span>,
        },
        {
            title: 'Trạng thái nợ',
            key: 'debtStatus',
            render: (_, record) => {
                const debt = debts.find(d => d.id === record.debtId);
                if (!debt) return <Tag color="default">Không tìm thấy</Tag>;

                return debt.daTra ?
                    <Tag color="success">Đã trả hết</Tag> :
                    <Tag color="processing">Còn nợ</Tag>;
            },
            width: 120,
        }
    ];

    return (
        <div className={styles.paymentHistory}>
            {/* Thống kê */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Tổng đã trả"
                            value={stats.totalPayments}
                            formatter={(value) => formatCurrency(value)}
                            valueStyle={{ color: '#52c41a' }}
                            prefix={<DollarOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Trả trong tháng"
                            value={stats.thisMonthPayments}
                            formatter={(value) => formatCurrency(value)}
                            valueStyle={{ color: '#1890ff' }}
                            prefix={<CalendarOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Số lần trả"
                            value={stats.paymentCount}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="Lần trả tháng này"
                            value={stats.thisMonthCount}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Bộ lọc */}
            <Card className={styles.filterCard}>
                <Space size="middle" wrap>
                    <Search
                        placeholder="Tìm kiếm theo tên, số tiền hoặc ghi chú..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 300 }}
                        allowClear
                    />

                    <RangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        format="DD/MM/YYYY"
                        placeholder={['Từ ngày', 'Đến ngày']}
                        allowClear
                    />
                </Space>
            </Card>

            {/* Bảng lịch sử */}
            <Card title="💳 Lịch sử thanh toán" className={styles.historyTable}>
                <Table
                    columns={columns}
                    dataSource={filteredPayments}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 15,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} của ${total} lần thanh toán`,
                        pageSizeOptions: ['10', '15', '25', '50'],
                    }}
                    scroll={{ x: 800 }}
                    size="middle"
                />
            </Card>
        </div>
    );
}
